import type {
  Channel, EpgEntry, Match, MatchInput, Rendition, ResolvedStream, Source, SourceInput, VariantId,
} from '@multiview/shared';

/**
 * No credential is kept in browser storage. Authentication is the session
 * cookie, which is httpOnly and therefore out of reach of any script on the
 * page -- including one injected into it. Same-origin requests carry it
 * automatically, so hls.js needs no special handling either.
 */

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // The session cookie is same-origin, so this is the default -- stated
    // explicitly because every authenticated call depends on it.
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface Fixture {
  provider: string;
  externalId: string;
  sport: string;
  competition: string;
  homeTeam: string;
  awayTeam: string;
  kickoff: number;
  status: string;
  homeScore: number | null;
  awayScore: number | null;
  /** Live details where the provider has them (FotMob, ESPN). */
  extra?: FixtureExtra | null;
  /** Stations FotMob lists for your country. */
  broadcasters?: string[];
  /** Crests, in the start page's listing (/api/fixtures). */
  homeLogo?: string | null;
  awayLogo?: string | null;
  /** Your channel for it, when that is certain enough to use without asking. */
  channel?: { id: number; name: string; reason: string } | null;
}

export interface FixtureExtra {
  clock: string | null;
  homeLogo: string | null;
  awayLogo: string | null;
  lastGoal: {
    minute: string | null;
    scorer: string | null;
    /** Only FotMob names the assist. */
    assist?: string | null;
    side: 'home' | 'away' | null;
    penalty: boolean;
    ownGoal: boolean;
    /** The score this goal made, where known. */
    score?: [number, number] | null;
    photo?: string | null;
  } | null;
  /** Cards and penalties so far (FotMob, 365Scores). */
  incidents?: Incident[];
}

export interface Incident {
  /** Stable across polls. */
  id: string;
  kind: 'yellow' | 'yellowred' | 'red' | 'penaltyMissed' | 'penaltyAwarded';
  side: 'home' | 'away';
  minute: string | null;
  player: string | null;
  photo?: string | null;
}

export interface StationChannel {
  channelId: number;
  name: string;
  group: string | null;
  kind: 'manual' | 'exact' | 'family';
  score: number;
}

export interface Station {
  stationId: string;
  name: string;
  type: string | null;
  country: string;
  matches: number;
  next: { homeTeam: string; awayTeam: string; start: number | null } | null;
  ignored: boolean;
  platform: boolean;
  manual: boolean;
  channels: StationChannel[];
  missing: Array<{ name: string; group: string }>;
}

/* Side panel data, mirrored from the proxy's fotmob.ts. */
export interface InfoEvent {
  kind: 'goal' | 'penalty' | 'owngoal' | 'yellow' | 'yellowred' | 'red' | 'penaltyMissed' | 'penaltyAwarded';
  side: 'home' | 'away';
  minute: number | null;
  player: string;
  playerId: string | null;
  assist: string | null;
  score: [number, number] | null;
  id: string;
}

export interface InfoLineupPlayer {
  id: string;
  name: string;
  number: string | null;
  positionId: number | null;
  rating: number | null;
  photo?: string | null;
}

export interface InfoTeamLineup {
  formation: string | null;
  starters: InfoLineupPlayer[];
  subs: InfoLineupPlayer[];
  coach: string | null;
}

export interface InfoMatch {
  id: string;
  leagueId: string | null;
  leagueName: string | null;
  kickoff: number | null;
  home: { id: string; name: string; score: number };
  away: { id: string; name: string; score: number };
  phase: number | null;
  firstHalfStart: number | null;
  secondHalfStart: number | null;
  events: InfoEvent[];
  lineups: { home: InfoTeamLineup; away: InfoTeamLineup } | null;
  venue: string | null;
}

export interface InfoTableRow {
  teamId: string;
  name: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  points: number;
  live: boolean;
}

export interface InfoPlayer {
  id: string;
  name: string;
  photo?: string | null;
  age: number | null;
  position: string | null;
  shirtNumber: string | null;
  country: string | null;
  team: string | null;
  heightCm: number | null;
  marketValueEur: number | null;
  season: { competition: string | null; matches: number | null; goals: number | null; assists: number | null; rating: number | null };
  recent: Array<{ date: number | null; opponent: string; competition: string | null; rating: number | null; goals: number; assists: number; minutes: number | null }>;
  traits: Array<{ title: string; percentile: number }>;
}

/** `ahead`: no state old enough was recorded, so this may be ahead of the picture. */
export interface InfoSnapshot<T> { data: T; at: number; ahead: boolean }

export interface ScoreEventDto {
  externalId: string;
  fixtureExternalId: string;
  kind: string;
  minute: number | null;
  scorer: string | null;
  homeScore: number;
  awayScore: number;
  side: string | null;
  /** When the proxy learned of it. The client waits for playback to reach this. */
  discoveredAt: number;
}

export interface RuntimeSettings {
  deinterlace: 'off' | 'auto';
}

export interface CapabilityStatus {
  available: boolean;
  reason: string | null;
  lastFault: string | null;
}

export interface SettingsView {
  settings: RuntimeSettings;
  /** `variants` is missing from a proxy that predates reduced renditions. */
  capabilities: { deinterlace: CapabilityStatus; variants?: CapabilityStatus };
}

/** One playback report; see apps/proxy/src/routes/telemetry.ts. */
export interface TelemetryReport {
  platform: string;
  build: number | null;
  display: string | null;
  /** Window and main-picture geometry in CSS px, to spot unwanted borders. */
  geo?: string;
  layout: string;
  cap: string;
  effective: string;
  step: number;
  audioGraph: boolean;
  seconds: number;
  tiles: Array<{
    slot: string; q: string; r: string | null; w: number; h: number;
    fps: number; drop: number; frames: number; buf: number; lat: number;
    /** Audio analysis state: level in dB, or why there is none. */
    au?: string;
  }>;
  event?: string;
}

export interface AuthState {
  needsSetup: boolean;
  user: { username: string; lastLogin: number | null } | null;
}

export interface Health {
  ok: boolean;
  hwaccel: string;
  tileHeight: number;
  maxSessions: number;
  /** Concurrent provider connections allowed, or null when unlimited. */
  upstreamBudget: number | null;
  dualOutput: boolean;
  authRequired: boolean;
  sessions: number;
}

export interface ChannelPage {
  channels: Channel[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChannelGroup {
  name: string;
  count: number;
}

export interface SessionStat {
  id: string;
  kind: 'remux' | 'transcode';
  inputUrl: string;
  uptimeMs: number;
  idleMs: number;
  restarts: number;
  alive: boolean;
}

export const api = {
  health: () => request<Health>('/api/health'),
  settings: () => request<SettingsView>('/api/settings'),
  updateSettings: (patch: Partial<RuntimeSettings>) =>
    request<SettingsView>('/api/settings', { method: 'PUT', body: JSON.stringify(patch) }),

  authState: () => request<AuthState>('/api/auth/state'),
  login: (username: string, password: string) =>
    request<{ user: { username: string } }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  setupAccount: (username: string, password: string) =>
    request<{ user: { username: string } }>('/api/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  changePassword: (current: string, next: string) =>
    request<{ ok: true }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ current, next }),
    }),

  listSources: () => request<Source[]>('/api/sources'),
  addSource: (input: SourceInput) =>
    request<{ id: number; channels: number }>('/api/sources', { method: 'POST', body: JSON.stringify(input) }),
  refreshSource: (id: number) =>
    request<{ channels: number }>(`/api/sources/${id}/refresh`, { method: 'POST' }),
  deleteSource: (id: number) => request<{ ok: true }>(`/api/sources/${id}`, { method: 'DELETE' }),

  listChannels: (params: {
    q?: string; sourceId?: number; group?: string; favoritesOnly?: boolean; ids?: string;
    limit?: number; offset?: number;
  } = {}) => {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '' && v !== false) search.set(k, String(v));
    }
    return request<ChannelPage>(`/api/channels?${search}`);
  },
  channelGroups: () => request<ChannelGroup[]>('/api/channels/groups'),
  setFavorite: (id: number, favorite: boolean) =>
    request<{ ok: true }>(`/api/channels/${id}/favorite`, { method: 'PUT', body: JSON.stringify({ favorite }) }),

  listMatches: () => request<Match[]>('/api/matches'),
  getMatch: (id: number) => request<Match>(`/api/matches/${id}`),
  createMatch: (input: MatchInput) =>
    request<Match>('/api/matches', { method: 'POST', body: JSON.stringify(input) }),
  updateMatch: (id: number, input: MatchInput) =>
    request<Match>(`/api/matches/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteMatch: (id: number) => request<{ ok: true }>(`/api/matches/${id}`, { method: 'DELETE' }),

  epg: (channelId: number) => request<{ entries: EpgEntry[] }>(`/api/epg?channelId=${channelId}`),
  epgNow: (channelIds: number[]) =>
    request<{ now: Record<number, { title: string; start: number; stop: number }> }>(
      `/api/epg/now?channelIds=${channelIds.join(',')}`,
    ),
  refreshEpg: () => request<{ entries: number; sources: number }>('/api/epg/refresh', { method: 'POST' }),

  scoreProviders: () =>
    request<{ providers: Array<{ id: string; label: string; configured: boolean }> }>('/api/scores/providers'),
  fixtures: (q?: string) =>
    request<{ fixtures: Fixture[] }>(`/api/fixtures${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  levels: (sessionIds: string[], since: number) =>
    request<{ now: number; levels: Record<string, Array<[number, number]>> }>(
      `/api/levels?s=${sessionIds.join(',')}&since=${since}`,
    ),
  ticker: () => request<{ now: number; fixtures: Fixture[] }>('/api/ticker'),
  refreshFixtures: () => request<{ fixtures: number }>('/api/fixtures/refresh', { method: 'POST' }),
  fixtureChannels: (provider: string, externalId: string) =>
    request<{
      fixture: { homeTeam: string; awayTeam: string; competition: string; kickoff: number };
      epgAvailable: boolean;
      suggestions: Array<{ channelId: number; name: string; group: string | null; reason: string; confidence: number }>;
    }>(`/api/fixtures/${encodeURIComponent(provider)}/${encodeURIComponent(externalId)}/channels`),
  stations: () => request<{ stations: Station[] }>('/api/stations'),
  refreshStations: () => request<{ broadcasts: number }>('/api/stations/refresh', { method: 'POST' }),
  updateStation: (id: string, update: { ignored?: boolean; channelIds?: number[] | null }) =>
    request<{ ok: true }>(`/api/stations/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(update) }),
  /** `fixtures` (`provider:externalId`) are the matches on screen; the proxy only polls those for goals. */
  scoreEvents: (since: number, fixtures: string[] = []) =>
    request<{ now: number; events: ScoreEventDto[] }>(
      `/api/score-events?since=${since}${fixtures.length > 0 ? `&fixtures=${encodeURIComponent(fixtures.join(','))}` : ''}`,
    ),

  /**
   * `want` names the variant this device uses for full-size slots. It goes
   * along with every request, tiles included, so that whichever request starts
   * a channel's session starts it with that variant already encoding.
   */
  resolve: (channelId: number, quality: Rendition, want: VariantId | null = null) =>
    request<ResolvedStream>(
      `/api/resolve?channelId=${channelId}&quality=${quality}${want ? `&want=${want}` : ''}`,
    ),

  infoMatch: (matchId: string, at: number) =>
    request<InfoSnapshot<InfoMatch>>(`/api/info/match/${encodeURIComponent(matchId)}?at=${Math.round(at)}`),
  infoTable: (leagueId: string, at: number) =>
    request<InfoSnapshot<Array<{ name: string; rows: InfoTableRow[] }>>>(
      `/api/info/table/${encodeURIComponent(leagueId)}?at=${Math.round(at)}`,
    ),
  infoPlayer: (playerId: string) => request<InfoPlayer>(`/api/info/player/${encodeURIComponent(playerId)}`),

  telemetry: (report: TelemetryReport) =>
    request<void>('/api/telemetry', { method: 'POST', body: JSON.stringify(report) }),

  sessions: () => request<{ sessions: SessionStat[]; max: number }>('/api/sessions'),
  releaseSession: (id: string) =>
    request<{ released: boolean }>(`/api/sessions/${encodeURIComponent(id)}/release`, { method: 'POST' }),
};
